import { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Eye, FileDown, Play, Video, Loader, AlertCircle } from 'lucide-react';
import { getGroupSuggestions, getCalendar, getMixedClassOpportunities, getConflicts, getScenarios, getVideoIntegrations } from '../services/scheduling';

export default function Ourdissage() {
  const [activeTab, setActiveTab] = useState('groups');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const tabs = [
    { id: 'groups', label: 'Regroupements', icon: Users },
    { id: 'calendar', label: 'Planning', icon: Calendar },
    { id: 'opportunities', label: 'Classes Mixtes', icon: Eye },
    { id: 'conflicts', label: 'Conflits', icon: AlertCircle },
    { id: 'simulation', label: 'Simulation', icon: Play },
    { id: 'visio', label: 'Visioconférence', icon: Video }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      let result;
      switch (activeTab) {
        case 'groups': result = await getGroupSuggestions(); break;
        case 'calendar': result = await getCalendar('2025-11-18', '2025-11-22'); break;
        case 'opportunities': result = await getMixedClassOpportunities(); break;
        case 'conflicts': result = await getConflicts(); break;
        case 'simulation': result = await getScenarios(); break;
        case 'visio': result = await getVideoIntegrations(); break;
      }
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeTab]);

  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-amber-600" /></div>;

    switch (activeTab) {
      case 'groups':
        return data?.suggestions?.map((group, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3 hover:shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-gray-900">{group.groupSize} apprenants</div>
                <div className="text-sm text-gray-600">Compétences: {group.competences.join(', ')}</div>
              </div>
              <div className="text-xl font-bold text-amber-600">{group.compatibility}%</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {group.learners.map((learner, j) => (
                <span key={j} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{learner}</span>
              ))}
            </div>
          </div>
        ));

      case 'calendar':
        return (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">{data?.calendar?.utilizationRate}%</div>
                <div className="text-sm text-gray-600">Taux d'utilisation des salles</div>
              </div>
            </div>
            {data?.calendar?.events?.map((event, i) => (
              <div key={i} className="border-l-4 border-blue-500 bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900">{event.title}</div>
                  <span className={`px-2 py-1 text-xs rounded-full ${event.type === 'course' ? 'bg-blue-100 text-blue-700' : event.type === 'workshop' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                    {event.type}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {new Date(event.start).toLocaleString('fr-FR')} • {event.instructor} • {event.room}
                </div>
              </div>
            ))}
          </div>
        );

      case 'opportunities':
        return data?.opportunities?.map((opp, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3 hover:border-amber-300">
            <div className="font-semibold text-gray-900 mb-2">{opp.sharedTopic}</div>
            <div className="text-sm text-gray-600 mb-2">
              Groupes: {opp.groups.join(', ')} • {opp.potentialLearners} apprenants
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              ✓ {opp.benefit}
            </div>
          </div>
        ));

      case 'conflicts':
        return data?.conflicts?.length > 0 ? (
          data.conflicts.map((conflict, i) => (
            <div key={i} className={`border-l-4 p-4 rounded-lg mb-3 ${conflict.severity === 'critical' ? 'border-red-500 bg-red-50' : 'border-orange-500 bg-orange-50'}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-5 h-5 flex-shrink-0 ${conflict.severity === 'critical' ? 'text-red-600' : 'text-orange-600'}`} />
                <div>
                  <div className="font-semibold mb-1">{conflict.type === 'room' ? 'Conflit de salle' : 'Conflit formateur'}</div>
                  <div className="text-sm">{conflict.message}</div>
                  <div className="text-xs text-gray-600 mt-1">{conflict.affectedSessions} sessions affectées</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-green-600">
            <div className="text-4xl mb-2">✓</div>
            <div className="font-semibold">Aucun conflit détecté</div>
          </div>
        );

      case 'simulation':
        return data?.scenarios?.map((scenario, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3 hover:border-amber-300 cursor-pointer">
            <div className="font-semibold text-gray-900 mb-1">{scenario.name}</div>
            <div className="text-sm text-gray-600 mb-2">{scenario.description}</div>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">{scenario.type}</span>
          </div>
        ));

      case 'visio':
        return data?.integrations?.map((integration, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-gray-900">{integration.platform}</div>
              <span className={`px-3 py-1 text-xs rounded-full ${integration.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {integration.status}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {integration.meetingsCreated} réunions créées • Dernière utilisation: {integration.lastUsed || 'Jamais'}
            </div>
          </div>
        ));

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Ourdissage</h1>
              <p className="text-gray-600">Emploi du temps - Optimisation pédagogique</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">{renderContent()}</div>
      </div>
    </div>
  );
}
